import * as RadixUiDialog from "@radix-ui/react-dialog";
import classNames from "classnames";
import React from "react";

import { PageDataLayer, tracking } from "../../app/services/tracking";
import { CloseButton } from "./CloseButton";
import { Head } from "./Head";
import { Markdown } from "./Markdown";
import { Overlay } from "./Overlay";

import AccountDesktopImage from "./DialogAccountDesktopImage.jpg";
import AccountMobileImage from "./DialogAccountMobileImage.jpg";
import ContractsDesktopImage from "./DialogContractsDesktopImage.jpg";
import ContractsMobileImage from "./DialogContractsMobileImage.jpg";
import InformationDesktopImage from "./DialogInformationDesktopImage.jpg";
import InformationMobileImage from "./DialogInformationMobileImage.jpg";

const classNamePrefix = "Dialog";

type DialogRootProps = RadixUiDialog.DialogProps;

const DialogRoot = (props: DialogRootProps) => (
  <RadixUiDialog.Root {...props} />
);

type DialogContentProps = Omit<RadixUiDialog.DialogContentProps, "title"> & {
  action?: React.ReactNode;
  closeLabel?: string;
  icon?: React.ReactNode;
  dataLayer?: PageDataLayer;
  meta?: { title: string };
  scrollRef?: React.ForwardedRef<HTMLDivElement>;
  search?: React.ReactNode;
  size?: "small" | "medium" | "large";
  title: React.ReactNode;
  variant?: "account" | "contracts" | "information";
};

const DialogContent = React.forwardRef(
  (props: DialogContentProps, ref?: React.ForwardedRef<HTMLDivElement>) => {
    const {
      action,
      "aria-describedby": ariaDescribedby = undefined,
      children,
      className,
      closeLabel = "Fermer",
      dataLayer,
      icon,
      meta,
      scrollRef,
      search,
      size,
      title,
      variant,
      onOpenAutoFocus,
      onScroll,
      ...restProps
    } = props;

    const image =
      variant &&
      {
        account: { desktop: AccountDesktopImage, mobile: AccountMobileImage },
        contracts: {
          desktop: ContractsDesktopImage,
          mobile: ContractsMobileImage,
        },
        information: {
          desktop: InformationDesktopImage,
          mobile: InformationMobileImage,
        },
      }[variant];

    return (
      <RadixUiDialog.Portal>
        <RadixUiDialog.Overlay asChild>
          <Overlay />
        </RadixUiDialog.Overlay>
        <RadixUiDialog.Content
          ref={ref}
          {...restProps}
          aria-describedby={ariaDescribedby}
          className={classNames(className, classNamePrefix)}
          data-icon={icon ? true : undefined}
          data-image={image ? true : undefined}
          data-size={size}
          data-variant={variant}
          onOpenAutoFocus={(event) => {
            onOpenAutoFocus?.(event);

            if (dataLayer) {
              tracking.trackPage(dataLayer);
            }
          }}
        >
          {meta && <Head {...meta} />}
          {image && (
            <div className={`${classNamePrefix}-image`}>
              <picture>
                <source media="(min-width: 960px)" srcSet={image.desktop} />
                <img alt="" src={image.mobile} />
              </picture>
            </div>
          )}
          <div className={`${classNamePrefix}-content`}>
            <div className={`${classNamePrefix}-header`}>
              {icon && <div className={`${classNamePrefix}-icon`}>{icon}</div>}
              <RadixUiDialog.Title asChild>
                <h1 className={`${classNamePrefix}-title`}>
                  <Markdown>{title}</Markdown>
                </h1>
              </RadixUiDialog.Title>
            </div>
            {search && (
              <div className={`${classNamePrefix}-search`}>{search}</div>
            )}
            <div
              ref={scrollRef}
              className={`${classNamePrefix}-body`}
              onScroll={onScroll}
            >
              {children}
            </div>
            {action && (
              <div className={`${classNamePrefix}-footer`}>{action}</div>
            )}
          </div>
          <RadixUiDialog.DialogClose asChild>
            <CloseButton
              aria-label={closeLabel}
              className={`${classNamePrefix}-close`}
            />
          </RadixUiDialog.DialogClose>
        </RadixUiDialog.Content>
      </RadixUiDialog.Portal>
    );
  },
);

type DialogTriggerProps = RadixUiDialog.DialogTriggerProps;

const DialogTrigger = React.forwardRef(
  (props: DialogTriggerProps, ref?: React.ForwardedRef<HTMLButtonElement>) => {
    const { asChild = true, ...restProps } = props;
    return <RadixUiDialog.Trigger ref={ref} {...restProps} asChild={asChild} />;
  },
);

export const Dialog = Object.assign(DialogRoot, {
  Content: DialogContent,
  Trigger: DialogTrigger,
});
